import React from 'react';
import '../../styles/timegraph.scss';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer
} from 'recharts';

interface HoursTracked {
  label: string;
  total_time: number;
  dateRange: string;
}

interface TimegraphProps {
  trackedHours?: HoursTracked[];
  chartLoading: boolean;
}


const CustomActiveDot: React.FC<any> = React.memo(({ cx, cy }) => {
  if (!cx || !cy) return null;

  return (
    <g>
      <line
        x1={cx}
        y1={cy}
        x2={cx}
        y2={250 - 35}
        stroke="#6BC07B"
        strokeWidth={2}
      />
      <circle cx={cx} cy={cy} r={8} fill="none" stroke="#6BC07B" strokeWidth={1.2} />
      <circle cx={cx} cy={cy} r={4} fill="#6BC07B" />
    </g>
  );
});

const CustomDot1: React.FC<any> = React.memo(({ cx, cy }) => {
  if (!cx || !cy) return null;

  return (
    <circle
      cx={cx}
      cy={cy}
      r={4}
      fill="#6BC07B"
      stroke="#6BC07B"
      strokeWidth={2}
    />
  );
});

const CustomTooltip: React.FC<any> = React.memo(({ active, payload, label }) => {
  if (!active || !payload || !payload.length) return null;

  const value = payload[0].value;

  return (
    <div
      style={{
        background: "#ffffff",
        border: "1px solid #E5E5E5",
        borderRadius: "8px",
        padding: "6px 12px",
        boxShadow: "0px 2px 6px rgba(0,0,0,0.08)",
        display: "flex",
        alignItems: "center",
        gap: "8px",
        fontFamily: "Helvetica Neue"
      }}
    >
      {/* Clock Icon SVG */}
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="14"
        height="14"
        viewBox="0 0 14 14"
        fill="none"
      >
        <path
          d="M6.9974 1.16602C5.84367 1.16602 4.71586 1.50813 3.75657 2.14911C2.79729 2.79008 2.04961 3.70113 1.6081 4.76703C1.16659 5.83293 1.05107 7.00582 1.27615 8.13738C1.50123 9.26893 2.0568 10.3083 2.87261 11.1241C3.68842 11.9399 4.72782 12.4955 5.85937 12.7206C6.99093 12.9457 8.16382 12.8302 9.22972 12.3886C10.2956 11.9471 11.2067 11.1995 11.8476 10.2402C12.4886 9.28089 12.8307 8.15307 12.8307 6.99935C12.829 5.4528 12.2138 3.9701 11.1202 2.87652C10.0266 1.78294 8.54395 1.16779 6.9974 1.16602ZM9.06981 8.68781L6.73647 7.52114C6.63958 7.47268 6.5581 7.3982 6.50115 7.30604C6.44421 7.21388 6.41405 7.10768 6.41406 6.99935V4.08268C6.41406 3.92797 6.47552 3.7796 6.58492 3.6702C6.69432 3.56081 6.84269 3.49935 6.9974 3.49935C7.15211 3.49935 7.30048 3.56081 7.40988 3.6702C7.51927 3.7796 7.58073 3.92797 7.58073 4.08268V6.63873L9.59166 7.64417C9.73005 7.71337 9.83529 7.83471 9.88422 7.98151C9.93314 8.1283 9.92175 8.28852 9.85255 8.42691C9.78335 8.56531 9.66201 8.67054 9.51521 8.71947C9.36842 8.7684 9.2082 8.75701 9.06981 8.68781Z"
          fill="#6BC07B"
        />
      </svg>

      <span
        style={{
          fontSize: "0.875rem",
          fontWeight: 500,
          color: "#121212",
        }}
      >
        {value} hr
      </span>

      {/* V-shaped pointer at bottom - positioned to touch the green dot */}
      <div
        style={{
          position: "absolute",
          bottom: "-6px",
          left: "50%",
          transform: "translateX(-50%)",
          width: 0,
          height: 0,
          borderLeft: "6px solid transparent",
          borderRight: "6px solid transparent",
          borderTop: "6px solid #ffffff",
        }}
      />
      {/* Border for the pointer */}
      <div
        style={{
          position: "absolute",
          bottom: "-7px",
          left: "50%",
          transform: "translateX(-50%)",
          width: 0,
          height: 0,
          borderLeft: "7px solid transparent",
          borderRight: "7px solid transparent",
          borderTop: "7px solid #E5E5E5",
          zIndex: -1,
        }}
      />
    </div>
  );
});

const Timegraph: React.FC<TimegraphProps> = ({ trackedHours = [], chartLoading }) => {
  const dynamicRange = trackedHours?.[0]?.dateRange || "";

  const chartData = React.useMemo(() => {
    return [...trackedHours]
      .reverse()
      .map(item => ({
        date: item.label,
        hours: item.total_time,
      }));
  }, [trackedHours]);

  // --- AUTO WIDTH FOR SCROLLING ---
  // const chartWidth = Math.max(chartData.length * 120, 600); // 120px per point
  const chartWidth = React.useMemo(() => {
    return Math.max(chartData.length * 120, 600);
  }, [chartData.length]);

  const [hoverStart, setHoverStart] = React.useState(false);
  const [hoverEnd, setHoverEnd] = React.useState(false);

  return (
    <div className="line-chart-ctr styled-scroll-hor">
      <div className="chart-section">
        <div className="chart-header">

          <h2>Time tracked trends</h2>
          <span className="date-range">{dynamicRange}</span>
        </div>

        {chartLoading ? (
          <div className="d-flex justify-content-center align-items-center crt-loader">
            {/* <Spinner animation="border"/> */}
            <img src="/images/loader/content_loader.gif" alt="Loading..." />
          </div>
        ) : (
          <div className="chart-scroll-x">
            <div className="chart-inner" style={{ width: chartWidth }}>
              <ResponsiveContainer width="100%" height={250}>
                <LineChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" vertical={false} />

                  <defs>
                    {/* Normal gradient */}
                    <linearGradient id="lineGradient" x1="0" y1="0" x2="1" y2="0">
                      <stop offset="0%" stopColor="#C6F2D0" />
                      <stop offset="50%" stopColor="#4F8C23" />
                      <stop offset="100%" stopColor="#C6F2D0" />
                    </linearGradient>

                    {/* Start hover - first half becomes dark */}
                    <linearGradient id="lineGradientStartHover" x1="0" y1="0" x2="1" y2="0">
                      <stop offset="0%" stopColor="#4F8C23" />
                      <stop offset="50%" stopColor="#4F8C23" />
                      <stop offset="100%" stopColor="#C6F2D0" />
                    </linearGradient>

                    {/* End hover - last half becomes dark */}
                    <linearGradient id="lineGradientEndHover" x1="0" y1="0" x2="1" y2="0">
                      <stop offset="0%" stopColor="#C6F2D0" />
                      <stop offset="50%" stopColor="#4F8C23" />
                      <stop offset="100%" stopColor="#4F8C23" />
                    </linearGradient>

                    {/* Both hover - entire line dark */}
                    <linearGradient id="lineGradientBothHover" x1="0" y1="0" x2="1" y2="0">
                      <stop offset="0%" stopColor="#4F8C23" />
                      <stop offset="50%" stopColor="#4F8C23" />
                      <stop offset="100%" stopColor="#4F8C23" />
                    </linearGradient>
                  </defs>

                  <XAxis
                    dataKey="date"
                    axisLine={false}
                    tickLine={false}
                    interval={0} // force display all dates
                    tick={{ fill: '#969696', fontSize: 14 }}
                    padding={{ left: 30, right: 30 }}   // Extra space on both ends
                  />

                  <YAxis
                    axisLine={false}
                    tickLine={false}
                    tick={{ fill: "#969696", fontSize: 14 }}
                    domain={['auto', 'auto']}
                  />

                  <Tooltip cursor={false} content={<CustomTooltip />} />

                  {/* Invisible overlay for start half hover detection */}
                  <rect
                    x="10%"
                    y="5"
                    width="40%"
                    height="180"
                    fill="transparent"
                    style={{ cursor: 'pointer' }}
                    onMouseEnter={() => setHoverStart(true)}
                    onMouseLeave={() => setHoverStart(false)}
                  />

                  {/* Invisible overlay for end half hover detection */}
                  <rect
                    x="50%"
                    y="5"
                    width="40%"
                    height="180"
                    fill="transparent"
                    style={{ cursor: 'pointer' }}
                    onMouseEnter={() => setHoverEnd(true)}
                    onMouseLeave={() => setHoverEnd(false)}
                  />

                  <Line
                    type="linear"
                    dataKey="hours"
                    stroke={
                      hoverStart && hoverEnd
                        ? "url(#lineGradientBothHover)"
                        : hoverStart
                          ? "url(#lineGradientStartHover)"
                          : hoverEnd
                            ? "url(#lineGradientEndHover)"
                            : "url(#lineGradient)"
                    }
                    strokeWidth={4}
                    dot={false}
                    activeDot={<CustomActiveDot />}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
export default React.memo(Timegraph);